import { QueryClient } from "@tanstack/react-query";
import { proxiedFetch } from "../../../../axios";
import { AxiosResponse } from "axios";

function findStyleImports(style: HTMLStyleElement) {
  return [...(style.sheet?.cssRules ?? [])]
    .filter((rule) => rule instanceof CSSImportRule);
}

function validateURL(url: string) {
  const { hostname } = new URL(url);
  if (['visrez.test', 'visrez.com'].some((domain) => hostname.endsWith(domain))) throw new Error('Refusing to fetch from our domains');
}

async function fetchStylesheet(source: { href: string }, queryClient: QueryClient, signal: AbortSignal): Promise<null | AxiosResponse<any, any>> {
  validateURL(source.href);

  return queryClient.fetchQuery({
    queryKey: ['manualIframe/fetchStylesheet', source.href, signal],
    queryFn: () => proxiedFetch(source.href, { signal }).catch(() => null)
  });
}

function processStylesheetResponses(responses: (null | AxiosResponse<any, any>)[], doc: Document, queryClient: QueryClient, signal: AbortSignal): Promise<any> {
  return Promise.all(
    responses.map(async (response) => {
      if (response === null) return null;
      if (!String(response.headers['content-type']).startsWith('text/css')) return null;

      const el = doc.createElement('style');
      el.innerHTML = response.data;
      doc.body.append(el);

      const subResponses = await Promise.all(
        findStyleImports(el)
          .map((source) => fetchStylesheet(source, queryClient, signal).catch(() => null))
      );

      return processStylesheetResponses(subResponses, doc, queryClient, signal);
    })
  );
}

/**
 * We want to effectively use an <iframe> to get access to a client page's DOM,
 * so we can extract styling information, however, for security, some have the
 * `X-Frame-Options` header set to `sameorigin`, therefore the browser refuses
 * to do it (since this would easily lead to clickjacking attacks). Therefore,
 * we need to fetch the URL and manually assemble the page. This way is fine,
 * since we don't get access to any cookies, but it's incomplete since we won't
 * execute their JS, so we could potentially miss some styles they inject with
 * JS. We do not care, this is a best effort and the client can just manually
 * set the font information if they want
 */
export default async function manualIframe(url: string, queryClient: QueryClient, signal: AbortSignal) {
  if (!url) return null;

  validateURL(url);

  const res = await proxiedFetch(url, { signal });

  if (!res || !String(res.headers['content-type']).startsWith('text/html')) throw new Error('Invalid URL');

  const parser = new DOMParser();
  const doc = parser.parseFromString(res.data, 'text/html');
  const base = doc.createElement('base');
  base.href = url;
  doc.head.prepend(base);

  const stylesheetLinks = doc.querySelectorAll('link[rel="stylesheet"]') as NodeListOf<HTMLLinkElement>;
  const styleImports = [...doc.querySelectorAll('style')].map(findStyleImports).flat();

  const stylesheetResponses = await Promise.all(
    [...stylesheetLinks, ...styleImports].map((source) => fetchStylesheet(source, queryClient, signal).catch(() => null))
  );

  await processStylesheetResponses(stylesheetResponses, doc, queryClient, signal);

  return { doc, proxyAuthToken: res.headers['x-proxy-auth-token'] as string };
}
